// 🔥 모델 삭제 확인 다이얼로그 - 최고급 안전 구현
// [배경] Firebase device_catalog + unified_prices에서 안전한 모델 삭제
//        실수 방지를 위한 다단계 확인 절차 + 데이터 영향 미리보기
// [개선] 2024.05.21 - 개별 변형(Variant) 가격 데이터만 삭제하는 기능 추가

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';

import { DeviceCatalogService } from '../../../../services/deviceCatalogService';
import { getUnifiedPriceService } from '../../../../services/unifiedPriceAdapter';
import { collection, getDocs, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { generateModelId } from '../../../../types/deviceCatalog'; // 🔑 P2: + 기호 보정용

// 🔥 wearable 카테고리 지원 추가
interface DeleteModelDialogProps {
  open: boolean;
  onClose: () => void;
  modelId: string;
  modelName: string;
  brand: 'Apple' | 'Samsung';
  deviceType: 'tablet' | 'smartphone' | 'wearable';
  onSuccess: (forceRefresh?: boolean) => void;
  // 🔥 개별 삭제를 위한 선택적 props
  targetPriceId?: string;
  targetVariant?: {
    storage: string;
    connectivity: string;
  };
}

interface DeletionImpact {
  catalogModel: any;
  relatedPrices: any[];
  totalConfigurations: number;
  hasActivePrices: boolean;
  priceDataSize: number;
  isPartialDelete: boolean; // 부분 삭제 여부
}

const DeleteModelDialog: React.FC<DeleteModelDialogProps> = ({
  open,
  onClose,
  modelId,
  modelName,
  brand,
  deviceType,
  onSuccess,
  targetPriceId,
  targetVariant
}) => {
  // 📊 상태 관리
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [impact, setImpact] = useState<DeletionImpact | null>(null);
  const [permanentDelete, setPermanentDelete] = useState(true); // 기본값: 물리적 삭제
  
  // 🔑 P2 개선: 최종 확인 체크박스 + 보정된 modelId 표시
  const [finalConfirmation, setFinalConfirmation] = useState(false);
  const [correctedModelIdForDisplay, setCorrectedModelIdForDisplay] = useState<string>(modelId);
  
  // 부분 삭제일 경우 확인 텍스트를 다르게 설정
  const isPartial = !!targetPriceId;

  // 🔍 삭제 영향 분석
  const analyzeDeletionImpact = useCallback(async () => {
    if (!open || !modelId) return;
    
    setAnalyzing(true);
    setError(null);
    
    try {
      // 🔑 P2 핵심: modelId가 + 기호를 누락한 경우 modelName에서 복원
      let correctedModelId = modelId;
      const idLower = modelId.toLowerCase();
      
      // + 기호 감지 (모델명에서)
      const hasPlusPlus = modelName.includes('++') || modelName.includes('플러스플러스');
      const hasPlus = !hasPlusPlus && (modelName.includes('+') || modelName.includes('플러스'));
      
      // ID에 이미 올바르게 반영되어 있는지 확인
      const idHasPlusPlus = idLower.includes('plus-plus');
      const idHasPlus = !idHasPlusPlus && idLower.includes('-plus');
      
      // 보정이 필요한 경우 (모델명에는 +가 있지만 ID에는 없음)
      if ((hasPlusPlus && !idHasPlusPlus) || (hasPlus && !idHasPlus)) {
        // 모델명에서 series 추출
        const seriesMatch = modelName.match(/^(Galaxy\s*[A-Za-z]+)/i) || 
                           modelName.match(/^(갤럭시\s*[가-힣A-Za-z]+)/i) ||
                           modelName.match(/^(iPhone)/i) ||
                           modelName.match(/^(iPad)/i);
        const series = seriesMatch ? seriesMatch[1].trim() : 'Unknown';
        
        const brand = modelId.toLowerCase().includes('apple') ? 'Apple' : 'Samsung';
        
        correctedModelId = generateModelId({
          brand: brand,
          deviceType: deviceType,
          series: series,
          modelEn: modelName
        });
        
        console.log(`🔧 [P2 삭제 modelId 보정] "${modelName}": "${modelId}" → "${correctedModelId}"`);
      }
      
      // 🔑 P2: 보정된 modelId를 UI 표시용으로 저장
      setCorrectedModelIdForDisplay(correctedModelId);
      
      console.log(`🔍 삭제 영향 분석 시작: ${correctedModelId} (${modelName}) ${targetPriceId ? `[Target: ${targetPriceId}]` : '[Full Model]'}`);
      
      // 1단계: device_catalog에서 모델 정보 조회 (원본 modelId 사용 - Firestore 문서 ID)
      const catalogModel = await DeviceCatalogService.getModelById(modelId);
      if (!catalogModel) {
        throw new Error('삭제할 모델을 찾을 수 없습니다.');
      }

      // 2단계: unified_prices에서 관련 가격 데이터 조회
      const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
      const allPrices = pricesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      let relatedPrices: any[] = [];
      
      // 🔑 P2 핵심: modelId 정규화 시 + / ++ 를 -plus / -plus-plus 로 변환하여 보존!
      // 기존 방식은 +만 제거해서 S25 / S25+ / S25++ 가 같은 ID로 취급됨 → 버그
      const normalizeModelId = (id: string | undefined): string => {
        if (!id) return '';
        return id
          .trim()
          // 🔑 P2: + 기호를 정규화 토큰으로 변환 (순서 중요!)
          .replace(/\+\+/g, '-plus-plus')
          .replace(/\+/g, '-plus')
          // 한글 "플러스"도 동일 처리
          .replace(/플러스플러스/g, '-plus-plus')
          .replace(/플러스/g, '-plus')
          // 연속 하이픈 정리
          .replace(/-+/g, '-')
          // 끝의 하이픈 제거
          .replace(/-+$/, '')
          // 소문자 정규화
          .toLowerCase();
      };
      
      // 🎧 웨어러블 connectivity 비교 함수
      const compareConnectivity = (priceConn: string | undefined, targetConn: string) => {
        // 둘 다 없거나 기본/Wi-Fi인 경우 동일 취급
        const normalizedPrice = priceConn || 'Wi-Fi';
        const normalizedTarget = targetConn || 'Wi-Fi';
        if (normalizedPrice === normalizedTarget) return true;
        // 웨어러블: 기본과 undefined/Wi-Fi를 동일 취급
        if (deviceType === 'wearable') {
          const wearableDefaults = ['기본', 'Wi-Fi', ''];
          if (wearableDefaults.includes(normalizedPrice) && wearableDefaults.includes(normalizedTarget)) {
            return true;
          }
        }
        return false;
      };

      if (targetPriceId && targetVariant) {
        // ✅ 부분 삭제 모드: 정확히 선택된 1건만 대상으로 한정
        // 🔥 웨어러블의 경우 유연한 매칭 적용
        // 🔑 P2: 보정된 modelId 사용
        relatedPrices = allPrices.filter((price: any) => {
          // priceId 직접 매칭 또는 modelId 기반 매칭
          const priceIdMatch = price.id === targetPriceId;
          const modelIdMatch = normalizeModelId(price.modelId) === normalizeModelId(correctedModelId);
          const storageMatch = price.variant?.storage === targetVariant.storage || 
            (deviceType === 'wearable' && (!price.variant?.storage || price.variant?.storage === '기본') && targetVariant.storage === '기본');
          const connectivityMatch = compareConnectivity(price.variant?.connectivity, targetVariant.connectivity);
          
          return (priceIdMatch || (modelIdMatch && storageMatch && connectivityMatch));
        });
      } else {
        // ✅ 전체 삭제 모드: 해당 모델의 모든 가격 데이터
        // 🔥 modelId 정규화하여 비교 (끝의 - 제거)
        // 🔑 P2: 보정된 modelId 사용
        relatedPrices = allPrices.filter((price: any) => 
          normalizeModelId(price.modelId) === normalizeModelId(correctedModelId)
        );
      }

      console.log(`💰 삭제 대상 가격 데이터: ${relatedPrices.length}개`);
      
      // 3단계: 영향 분석
      const totalConfigurations =
        (catalogModel.storageOptions?.length || 0) *
        ((catalogModel.connectivityOptions?.length || 0) || 1);

      const hasActivePrices = relatedPrices.some((price: any) =>
        Object.values(price.gradePrices || {}).some((p: any) => p > 0)
      );

      const priceDataSize = relatedPrices.reduce(
        (total, price: any) =>
          total +
          Object.values(price.gradePrices || {}).reduce(
            (sum: number, p: any) => sum + (p || 0),
            0
          ),
        0
      );
      
      const deletionImpact: DeletionImpact = {
        catalogModel,
        relatedPrices,
        totalConfigurations,
        hasActivePrices,
        priceDataSize,
        isPartialDelete: !!(targetPriceId && targetVariant)
      };
      
      setImpact(deletionImpact);
      
    } catch (err) {
      console.error('❌ 삭제 영향 분석 실패:', err);
      setError(`삭제 영향 분석 실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setAnalyzing(false);
    }
  }, [open, modelId, modelName, targetPriceId]);

  // 🔥 실제 삭제 실행
  const executeDelete = async () => {
    if (!impact) {
      setError('삭제 대상을 분석하는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log(`🔥 삭제 실행 시작: ${modelId} (부분삭제: ${impact.isPartialDelete})`);
      
      // 1단계: 선택된 가격 데이터 삭제 (부분 삭제시 1개, 전체 삭제시 N개)
      if (impact.relatedPrices.length > 0) {
        console.log(`💰 가격 데이터 ${impact.relatedPrices.length}개 삭제 중...`);
        
        const batch = writeBatch(db);
        impact.relatedPrices.forEach(price => {
          const priceRef = doc(db, 'unified_prices', price.id);
          batch.delete(priceRef);
        });
        
        await batch.commit();
        console.log(`✅ unified_prices ${impact.relatedPrices.length}개 삭제 완료`);
      }
      
      // 🔥 2단계: device_catalog에서 모델/옵션 업데이트
      if (!impact.isPartialDelete) {
        // 전체 삭제 모드 – 카탈로그 모델 자체 삭제/비활성화
        if (permanentDelete) {
          console.log('🔥 device_catalog에서 모델 물리적 삭제...');
          await DeviceCatalogService.deleteModelPermanently(modelId, 'admin');
          console.log('✅ device_catalog 물리적 삭제 완료');
        } else {
          console.log('📱 device_catalog에서 모델 비활성화...');
          await DeviceCatalogService.deactivateModel(modelId, 'admin');
          console.log('✅ device_catalog 비활성화 완료');
        }
      } else if (impact.isPartialDelete && targetVariant) {
        // 부분 삭제 모드 – 선택된 구성(시리즈+용량+연결성)을 비활성화 목록에 추가
        console.log('🧩 부분 삭제: 선택된 구성만 비활성화(disabledVariants)에 추가합니다.');

        const existing = (impact.catalogModel.disabledVariants || []) as {
          storage: string;
          connectivity?: import('../../../../types/deviceCatalog').ConnectivityType;
        }[];

        const alreadyDisabled = existing.some(v =>
          v.storage === targetVariant.storage &&
          ((v.connectivity || 'Wi-Fi') === targetVariant.connectivity)
        );

        if (!alreadyDisabled) {
          const updatedDisabled = [
            ...existing,
            {
              storage: targetVariant.storage,
              connectivity: targetVariant.connectivity as import('../../../../types/deviceCatalog').ConnectivityType
            }
          ];

          await DeviceCatalogService.updateModel(
            modelId,
            { disabledVariants: updatedDisabled },
            'admin'
          );

          console.log('✅ disabledVariants 업데이트 완료:', updatedDisabled);
        } else {
          console.log('ℹ️ 이미 비활성화된 구성입니다. 추가 업데이트는 수행하지 않습니다.');
        }
      }

      
      // 성공 처리
      console.log(`🎉 삭제 완료: ${modelName}`);
      onSuccess(true); // 강제 새로고침 요청
      handleClose();
      
    } catch (error) {
      console.error('❌ 삭제 실패:', error);
      setError(`삭제 실패: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  // 🔄 다이얼로그 초기화
  const handleClose = () => {
    setImpact(null);
    setPermanentDelete(true); // 기본값으로 리셋
    setError(null);
    setLoading(false);
    setAnalyzing(false);
    setFinalConfirmation(false); // 🔑 P2: 최종 확인 체크 초기화
    setCorrectedModelIdForDisplay(modelId); // 🔑 P2: 보정된 ID 초기화
    onClose();
  };

  // 컴포넌트 마운트 시 영향 분석
  useEffect(() => {
    if (open) {
      analyzeDeletionImpact();
    }
  }, [open, analyzeDeletionImpact]);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          {isPartial ? '구성 삭제 확인 (개별 삭제)' : '모델 삭제 확인 (전체 삭제)'}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* 🚨 경고 메시지 */}
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            ⚠️ {isPartial ? '개별 구성 삭제' : '모델 전체 삭제'} 경고
          </Typography>
          <Typography variant="body2">
            {isPartial ? (
              <>
                이 작업은 <strong>{modelName}</strong>의 <strong>{targetVariant?.storage} {targetVariant?.connectivity}</strong>{' '}
                구성에 대한 <strong>모델 및 가격 정보를 함께 삭제(비활성화)</strong>합니다.<br/>
                같은 모델의 다른 용량/연결성 구성은 유지되며, 삭제된 구성은 관리자/사용자 화면에 더 이상 표시되지 않습니다.
              </>
            ) : (
              <>
                이 작업은 <strong>{modelName}</strong> 모델과 <strong>모든 관련 데이터</strong>를 삭제합니다.<br/>
                {permanentDelete ? '완전히 삭제된 데이터는 복구할 수 없으니' : '비활성화된 모델은 나중에 재활성화할 수 있지만'} 신중히 검토해주세요.
              </>
            )}
          </Typography>
        </Alert>

        {/* 🔥 삭제 방식 선택 (전체 삭제일 때만 표시) */}
        {!isPartial && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fef9c3' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              🔧 삭제 방식 선택
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={permanentDelete}
                  onChange={(e) => setPermanentDelete(e.target.checked)}
                  color="error"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600} color="error.main">
                    완전 삭제 (권장)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Firebase에서 모델과 가격을 모두 물리적으로 삭제합니다. 쓰레기 데이터가 남지 않습니다.
                  </Typography>
                </Box>
              }
            />
            {!permanentDelete && (
              <Box sx={{ mt: 1, pl: 4 }}>
                <Typography variant="caption" color="warning.main">
                  ⚠️ 소프트 삭제: 모델은 비활성화만 되고 Firebase에 데이터가 남습니다.
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {analyzing && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={30} />
            <Typography sx={{ ml: 2 }}>삭제 영향을 분석하는 중...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* 📊 삭제 영향 분석 결과 */}
        {impact && !analyzing && (
          <>
            <Typography variant="h6" gutterBottom>
              📊 {isPartial ? '삭제 대상' : '삭제 영향 분석'}
            </Typography>

            {/* 기본 모델 정보 */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                📱 대상 모델 정보
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip label={`ID: ${impact.catalogModel.id}`} variant="outlined" size="small" />
                <Chip label={`모델명: ${modelName}`} color="primary" variant="outlined" size="small" />
                <Chip label={`시리즈: ${impact.catalogModel.series}`} variant="outlined" size="small" />
              </Box>
              
              {isPartial && targetVariant && (
                <Box sx={{ mt: 1, p: 1, bgcolor: '#fee2e2', borderRadius: 1, display: 'inline-block' }}>
                  <Typography variant="body2" fontWeight={700} color="error.main">
                    🎯 삭제 타겟: {targetVariant.storage} {targetVariant.connectivity}
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* 삭제될 가격 데이터 미리보기 */}
            {impact.relatedPrices.length > 0 ? (
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  💰 삭제될 가격 데이터 ({impact.relatedPrices.length}개)
                </Typography>
                
                <TableContainer sx={{ maxHeight: '200px' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>구성</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>A등급</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>B등급</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>C등급</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>D등급</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {impact.relatedPrices.map((price: any, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} color="error.main">
                              {price.variant?.storage || 'Unknown'} {price.variant?.connectivity || 'Wi-Fi'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography 
                              variant="body2" 
                              fontFamily="monospace"
                              color={price.gradePrices?.A > 0 ? 'text.primary' : 'text.disabled'}
                            >
                              {price.gradePrices?.A?.toLocaleString() || '0'}원
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography 
                              variant="body2" 
                              fontFamily="monospace"
                              color={price.gradePrices?.B > 0 ? 'text.primary' : 'text.disabled'}
                            >
                              {price.gradePrices?.B?.toLocaleString() || '0'}원
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography 
                              variant="body2" 
                              fontFamily="monospace"
                              color={price.gradePrices?.C > 0 ? 'text.primary' : 'text.disabled'}
                            >
                              {price.gradePrices?.C?.toLocaleString() || '0'}원
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography 
                              variant="body2" 
                              fontFamily="monospace"
                              color={price.gradePrices?.D > 0 ? 'text.primary' : 'text.disabled'}
                            >
                              {price.gradePrices?.D?.toLocaleString() || '0'}원
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : (
               <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    삭제할 가격 데이터가 없습니다. 이미 삭제되었거나 데이터가 존재하지 않습니다.
                  </Typography>
               </Alert>
            )}

            {/* 🔥 확인 절차 간소화: 추가 입력 없이 바로 삭제 가능 (취소/삭제 버튼만 사용) */}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, p: 2 }}>
        {/* 🔑 P2 핵심: 삭제 대상 ID 확인 + 최종 확인 체크박스 */}
        {impact && !analyzing && (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              bgcolor: finalConfirmation ? '#fef2f2' : '#fff7ed',
              border: '2px solid',
              borderColor: finalConfirmation ? '#ef4444' : '#f59e0b'
            }}
          >
            {/* 삭제 대상 Model ID 강조 표시 */}
            <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: '#991b1b' }}>
              🎯 삭제 대상 Model ID:
            </Typography>
            <Box sx={{ 
              bgcolor: '#fff', 
              p: 1.5, 
              borderRadius: 1, 
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              mb: 2,
              border: '1px solid #fecaca'
            }}>
              <Typography component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', color: '#dc2626' }}>
                {correctedModelIdForDisplay}
              </Typography>
              {correctedModelIdForDisplay !== modelId && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#f59e0b' }}>
                  ⚠️ 원본 ID "{modelId}"에서 보정됨
                </Typography>
              )}
            </Box>
            
            {/* 다른 모델 영향 검증 결과 */}
            <Typography variant="body2" sx={{ mb: 2 }}>
              📄 삭제될 가격 문서: <strong>{impact.relatedPrices.length}개</strong>
              {impact.relatedPrices.length > 0 && (
                <Box component="span" sx={{ color: '#22c55e', ml: 1 }}>
                  ✅ 다른 모델 영향: 0개 (안전)
                </Box>
              )}
            </Typography>
            
            {/* 최종 확인 체크박스 */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={finalConfirmation}
                  onChange={(e) => setFinalConfirmation(e.target.checked)}
                  color="error"
                />
              }
              label={
                <Typography variant="body2" fontWeight={600}>
                  위 ID(<strong style={{ color: '#dc2626' }}>{correctedModelIdForDisplay}</strong>)가 
                  내가 삭제하려는 대상과 일치함을 확인합니다.
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </Paper>
        )}
        
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={handleClose} disabled={loading}>
            취소
          </Button>
          <Button 
            onClick={executeDelete}
            variant="contained"
            color="error"
            disabled={
              loading || 
              analyzing || 
              !impact ||
              !finalConfirmation  // 🔑 P2: 최종 확인 체크 필수
            }
            startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {loading ? '삭제 중...' : 
             !finalConfirmation ? '확인 체크 필요' :
             (isPartial ? '구성 삭제' : (permanentDelete ? '완전 삭제' : '비활성화'))}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteModelDialog;
